import { Injectable } from '@angular/core';
import { Logger } from '@pfizer/newton-jetpack-utils';
import { FirebaseApp, initializeApp } from 'firebase/app';
import { initializePerformance } from 'firebase/performance';
import { RemoteConfig, Value, ensureInitialized, fetchAndActivate, getAll, getRemoteConfig } from 'firebase/remote-config';
import { environment } from '../../environments/environment';
import { UserStore } from '../stores/user.store';
import type { RemoteConfigProps } from './firebase.service.const';
import { Capacitor } from '@capacitor/core';

@Injectable({
  providedIn: 'root'
})
export class FirebaseService {
  private readonly firebaseApp: FirebaseApp;
  private readonly remoteConfig: RemoteConfig;

  constructor(
    private readonly logger: Logger,
    private readonly userStore: UserStore
  ) {

    if(Capacitor.getPlatform() !== 'web'){
      this.logger.error(
        '[FirebaseService] This project is using the demo Firebase instance jetpack-demo-us-dev.',
        "Remember to update Ionic, iOS, and Android to use your project's real configuration.",
        'See Newton Docs for details: https://docs.platforms.pfizer/newton/docs/newton-jetpack/schematics/firebase/'
      );

      this.firebaseApp = initializeApp(environment.firebase);

      this.remoteConfig = {
        ...getRemoteConfig(this.firebaseApp),
        settings: {
          fetchTimeoutMillis: 30000,
          minimumFetchIntervalMillis: 2000
        }
      };

      initializePerformance(this.firebaseApp);

      this.init();
    }
  }

  private init() {
    this.refresh();
  }

  /**
   * Fetches the most recent [remote configuration](https://firebase.google.com/docs/remote-config)
   * from your Firebase instance and stores it locally in the {@linkcode UserStore}.
   *
   * @example
   *
   *  ```ts
   * await this.firebase.refresh();
   * // => { newton: { ... }, project: { ... }}
   * ```
   */
  public async refresh(): Promise<RemoteConfigProps> {
    await this.userStore.isReady().catch((msg: unknown) => this.logger.warn(msg));

    await fetchAndActivate(this.remoteConfig).catch((msg: unknown) => this.logger.warn(msg));
    await ensureInitialized(this.remoteConfig).catch((msg: unknown) => this.logger.warn(msg));

    const remoteConfig = this.parse(getAll(this.remoteConfig));

    this.userStore.updateUser({ remoteConfig });

    return remoteConfig;
  }

  private parse(response: Record<string, Value>): RemoteConfigProps {
    return Object.fromEntries(
      Object.entries(response).map(([key, value]) => [key, JSON.parse(value.asString())])
    ) as RemoteConfigProps;
  }
}
